import React from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Church, Heart, Users, TrendingUp } from 'lucide-react';
export function CopticPeoplePage() {
  return <>
      {/* Hero Section */}
      <Section className="py-20" background="offwhite" data-id="element-371">
        <div className="text-center mb-12" data-id="element-372">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-373">
            Understanding Egypt's Coptic Christians
          </h1>
          <p className="text-xl font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-374">
            A 2,000-year legacy of faith, resilience, and community service.
          </p>
        </div>
      </Section>

      {/* Section 1: Ancient Roots, Modern Struggles */}
      <Section background="white" data-id="element-375">
        <div className="grid md:grid-cols-2 gap-12 items-center mb-12" data-id="element-376">
          <div data-id="element-377">
            <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-378">
              Ancient Roots, Modern Struggles
            </h2>
            <div className="space-y-4 text-lg font-inter text-neutral-charcoal" data-id="element-379">
              <p data-id="element-380">
                Coptic Christians are the indigenous Christians of Egypt,
                tracing their faith to Saint Mark the Evangelist who arrived in
                Alexandria around 42 AD—within a decade of Christ's crucifixion.
                The word "Copt" itself derives from the Greek "Aigyptos"
                (Egypt); to be Coptic is, linguistically and historically, to be
                Egyptian.
              </p>
              <p data-id="element-381">
                For nearly 2,000 years, Copts have maintained their faith
                through Roman persecution, Arab conquest, and cycles of
                tolerance and oppression. Their calendar begins not from
                Christ's birth but from 284 AD—the "Era of Martyrs"—when Emperor
                Diocletian's persecution killed so many that Copts reset time
                itself to honor them.
              </p>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg" data-id="element-382">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3sXttJzU9VsgXRAP87D2ux/2h-media-8LSrZI3VaYA-unsplash.jpg" alt="Historic Coptic church in Egypt" className="w-full h-full object-cover" data-id="element-383" />
          </div>
        </div>
        <div className="max-w-4xl mx-auto" data-id="element-384">
          <p className="text-lg font-inter text-neutral-charcoal" data-id="element-385">
            Today, Copts represent the largest Christian community in the Middle
            East, numbering between 9-12 million people (roughly 10% of Egypt's
            population). They are the living descendants of the Pharaohs,
            speaking a liturgical language directly descended from ancient
            Egyptian.
          </p>
        </div>
      </Section>

      {/* Visual Break - Monastery Architecture */}
      <Section background="sand" className="py-12" data-id="element-386">
        <div className="grid md:grid-cols-2 gap-6" data-id="element-387">
          <div className="rounded-xl overflow-hidden shadow-lg" data-id="element-388">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kHgcywjBFUP6wyHSdrkbJv/2h-media-FLp7yMlPV3w-unsplash.jpg" alt="Coptic monastery dome and architecture" className="w-full h-80 object-cover" data-id="element-389" />
          </div>
          <div className="rounded-xl overflow-hidden shadow-lg" data-id="element-390">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/mERH9XE8aqyk8z6yq7zb8x/rafik-dDXjeRJz01k-unsplash.jpg" alt="Ancient Coptic monastery pathway" className="w-full h-80 object-cover" data-id="element-391" />
          </div>
        </div>
      </Section>

      {/* Section 2: Birthplace of Monasticism */}
      <Section background="white" data-id="element-392">
        <div className="max-w-4xl mx-auto mb-12" data-id="element-393">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-394">
            Birthplace of Monasticism
          </h2>
          <div className="space-y-4 text-lg font-inter text-neutral-charcoal mb-8" data-id="element-395">
            <p data-id="element-396">
              Egypt didn't just adopt monasticism—Egypt invented it. When Saint
              Anthony the Great withdrew to the desert around 270 AD, he
              established a pattern that would spread throughout global
              Christianity. By the fourth century, the historian Athanasius
              wrote that "the desert had become a city" with thousands of monks.
            </p>
            <p data-id="element-397">
              At its peak, the Wadi Natrun valley alone contained 700
              monasteries housing thousands of monks. Today, Egypt maintains
              approximately 11 active desert monasteries—including the Monastery
              of Saint Anthony, the oldest continuously inhabited Christian
              monastery in the world.
            </p>
            <p data-id="element-398">
              These aren't historical curiosities. Modern Egyptian monks are
              often educated professionals—engineers, doctors,
              pharmacists—who've chosen monastic life. Monasteries serve as:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-399">
            {[{
            icon: <Church size={32} className="text-primary-royal-blue" data-id="element-400" />,
            title: 'Spiritual Centers',
            description: 'Hosting over a million pilgrims annually'
          }, {
            icon: <Heart size={32} className="text-primary-royal-blue" data-id="element-401" />,
            title: 'Cultural Repositories',
            description: 'Preserving ancient manuscripts and art'
          }, {
            icon: <Users size={32} className="text-primary-royal-blue" data-id="element-402" />,
            title: 'Community Anchors',
            description: 'Providing services to surrounding villages'
          }, {
            icon: <TrendingUp size={32} className="text-primary-royal-blue" data-id="element-403" />,
            title: 'Leadership Sources',
            description: 'Most Coptic bishops come from monasteries'
          }].map((item, index) => <div key={index} className="bg-neutral-offwhite p-6 rounded-xl shadow-md" data-id="element-404">
                <div className="mb-4" data-id="element-405">{item.icon}</div>
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-2" data-id="element-406">
                  {item.title}
                </h3>
                <p className="font-inter text-neutral-charcoal" data-id="element-407">
                  {item.description}
                </p>
              </div>)}
          </div>
        </div>
      </Section>

      {/* Visual Break - Interior Beauty */}
      <Section background="sand" className="py-12" data-id="element-408">
        <div className="max-w-5xl mx-auto" data-id="element-409">
          <div className="rounded-xl overflow-hidden shadow-xl" data-id="element-410">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/94Ua5NgUDwuz2f6CTDtuJN/lisa-forkner-J_LRBSIla0c-unsplash.jpg" alt="Beautiful Coptic church interior with iconography" className="w-full h-96 object-cover" data-id="element-411" />
          </div>
          <p className="text-center font-inter text-neutral-charcoal mt-4 italic" data-id="element-412">
            Coptic churches preserve centuries of artistic and spiritual
            heritage
          </p>
        </div>
      </Section>

      {/* Section 3: Healthcare Gaps */}
      <Section background="white" data-id="element-413">
        <div className="max-w-4xl mx-auto" data-id="element-414">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-415">
            Healthcare Gaps in the Desert
          </h2>
          <div className="space-y-4 text-lg font-inter text-neutral-charcoal mb-8" data-id="element-416">
            <p data-id="element-417">
              Egypt's healthcare system faces significant challenges, with over
              56% of health expenditure coming from out-of-pocket payments—among
              the highest rates globally. The divide between urban and rural
              healthcare is stark: while Cairo has world-class private
              hospitals, rural Upper Egypt—home to the largest Coptic
              populations—lacks basic medical infrastructure.
            </p>
            <p data-id="element-418">
              Monastery clinics fill crucial gaps. Located hours from major
              hospitals, they serve both monastic communities and surrounding
              villages. These clinics operate with:
            </p>
          </div>

          <div className="bg-neutral-offwhite p-8 rounded-xl mb-8" data-id="element-419">
            <ul className="space-y-3 text-lg font-inter text-neutral-charcoal" data-id="element-420">
              <li className="flex items-start" data-id="element-421">
                <span className="text-primary-royal-blue mr-3 mt-1" data-id="element-422">•</span>
                <span data-id="element-423">Volunteer and visiting practitioners</span>
              </li>
              <li className="flex items-start" data-id="element-424">
                <span className="text-primary-royal-blue mr-3 mt-1" data-id="element-425">•</span>
                <span data-id="element-426">Minimal and often outdated equipment</span>
              </li>
              <li className="flex items-start" data-id="element-427">
                <span className="text-primary-royal-blue mr-3 mt-1" data-id="element-428">•</span>
                <span data-id="element-429">Chronic supply shortages</span>
              </li>
              <li className="flex items-start" data-id="element-430">
                <span className="text-primary-royal-blue mr-3 mt-1" data-id="element-431">•</span>
                <span data-id="element-432">Limited dental services despite high need</span>
              </li>
            </ul>
          </div>

          <p className="text-lg font-inter text-neutral-charcoal" data-id="element-433">
            When we asked what they needed most, monastery leaders didn't
            request money—they requested supplies. Medications, dental
            equipment, diagnostic tools, basic consumables. Items readily
            available in America but difficult to source in Egypt's desert.
          </p>
        </div>
      </Section>

      {/* Section 4: Why This Matters */}
      <Section background="sand" data-id="element-434">
        <div className="max-w-4xl mx-auto" data-id="element-435">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-436">
            Why This Matters Beyond Religion
          </h2>
          <div className="space-y-4 text-lg font-inter text-neutral-charcoal mb-8" data-id="element-437">
            <p data-id="element-438">
              Supporting Coptic monastery healthcare isn't about religious
              favoritism—it's about reaching underserved communities through
              existing infrastructure. Monastery clinics already have
              practitioners, facilities, and community trust. They lack only
              supplies.
            </p>
            <p data-id="element-439">Every unit of medical equipment we deliver serves:</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-440">
            {['Monks and monastery residents with ongoing health needs', 'Pilgrims and visitors (over 1 million annually) who may need medical attention', 'Surrounding village populations with limited healthcare alternatives', 'The preservation of communities maintaining 2,000 years of continuous Christian presence'].map((item, index) => <div key={index} className="bg-white p-4 rounded-lg shadow-sm" data-id="element-441">
                <p className="font-inter text-neutral-charcoal" data-id="element-442">{item}</p>
              </div>)}
          </div>

          <p className="text-lg font-inter text-neutral-charcoal" data-id="element-443">
            By partnering directly with monastery leadership, we ensure supplies
            reach those who need them—without bureaucratic intermediaries or
            diversion.
          </p>
        </div>
      </Section>

      {/* Section 5: How You Can Help */}
      <Section background="deep-blue" data-id="element-444">
        <div className="text-center max-w-3xl mx-auto" data-id="element-445">
          <h2 className="text-3xl font-playfair font-bold mb-6" data-id="element-446">
            How You Can Help
          </h2>
          <p className="text-xl font-inter mb-8" data-id="element-447">
            Understanding this history transforms giving from charity into
            partnership. You're not just donating medical supplies—you're
            supporting communities that have survived two millennia of
            challenges through resilience, faith, and mutual aid.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-448">
            <Button href="https://www.paypal.com/biz/profile/FarashaMedicalAid" size="lg" data-id="element-449">
              Donate Now
            </Button>
            <Button href="/blog" variant="secondary" size="lg" data-id="element-450">
              Read Our Blog
            </Button>
          </div>
        </div>
      </Section>
    </>;
}